import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, TrendingUp, Download, Share2, BookOpen, ArrowRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const ISvsIRSimulator = () => {
  const [inputs, setInputs] = useState({
    benefice: '60000',
    parts: '1',
    dividendes: '20000'
  });
  const [results, setResults] = useState({});

  const calcIR = (revenuImposable, parts) => {
    const quotient = revenuImposable / parts;
    let impot = 0;
    if (quotient > 181917) impot += (quotient - 181917) * 0.45;
    if (quotient > 84577) impot += (Math.min(quotient, 181917) - 84577) * 0.41;
    if (quotient > 29579) impot += (Math.min(quotient, 84577) - 29579) * 0.30;
    if (quotient > 11600) impot += (Math.min(quotient, 29579) - 11600) * 0.11;
    return Math.round(impot * parts);
  };

  const calcIS = (benefice) => {
    if (benefice <= 0) return 0;
    const base15 = Math.min(benefice, 42500);
    const base25 = Math.max(benefice - 42500, 0);
    return Math.round(base15 * 0.15 + base25 * 0.25);
  };

  const calculate = () => {
    const benefice = parseFloat(inputs.benefice) || 0;
    const parts = parseFloat(inputs.parts) || 1;
    const dividendes = parseFloat(inputs.dividendes) || 0;

    // Option IR : le bénéfice est directement imposé à l'IR
    const impotIR = calcIR(benefice, parts);
    const netIR = benefice - impotIR;
    const tauxIR = benefice > 0 ? (impotIR / benefice * 100) : 0;

    // Option IS : IS sur le bénéfice, puis flat tax sur dividendes
    const impotIS = calcIS(benefice);
    const resultatNet = benefice - impotIS;
    const dividendesEffectifs = Math.min(dividendes, resultatNet);
    const flatTax = dividendesEffectifs * 0.30;
    const dividendesNets = dividendesEffectifs - flatTax;
    const resteEnSociete = resultatNet - dividendesEffectifs;
    const totalImpotsIS = impotIS + flatTax;
    const netIS = dividendesNets + resteEnSociete;
    const tauxIS = benefice > 0 ? (totalImpotsIS / benefice * 100) : 0;

    const avantageIS = netIS - netIR;

    setResults({
      impotIR, netIR, tauxIR: tauxIR.toFixed(1),
      impotIS, flatTax: Math.round(flatTax), dividendesNets: Math.round(dividendesNets),
      resteEnSociete: Math.round(resteEnSociete), totalImpotsIS: Math.round(totalImpotsIS),
      netIS: Math.round(netIS), tauxIS: tauxIS.toFixed(1),
      avantageIS: Math.round(avantageIS),
      meilleur: avantageIS > 500 ? 'IS' : avantageIS < -500 ? 'IR' : 'Similaire'
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'IS vs IR - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Comparaison IS vs IR', 15, 25);
    doc.setFontSize(10); doc.text('Simulation réalisée sur blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(14); doc.text('Paramètres', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`Bénéfice : ${parseInt(inputs.benefice).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Parts fiscales : ${inputs.parts}`, 15, y); y += 7;
    doc.text(`Dividendes prévus : ${parseInt(inputs.dividendes).toLocaleString()} EUR`, 15, y); y += 15;
    doc.setFontSize(14); doc.text('Option IR (Impôt sur le Revenu)', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`Impôt IR : ${results.impotIR?.toLocaleString()} EUR (${results.tauxIR}%)`, 15, y); y += 7;
    doc.text(`Net après IR : ${results.netIR?.toLocaleString()} EUR`, 15, y); y += 15;
    doc.setFontSize(14); doc.text('Option IS (Impôt sur les Sociétés)', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`IS : ${results.impotIS?.toLocaleString()} EUR + Flat tax : ${results.flatTax?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Total impositions : ${results.totalImpotsIS?.toLocaleString()} EUR (${results.tauxIS}%)`, 15, y); y += 7;
    doc.text(`Net global : ${results.netIS?.toLocaleString()} EUR`, 15, y); y += 12;
    doc.setFontSize(13); doc.setTextColor(30, 64, 175);
    doc.text(`Recommandation : ${results.meilleur}`, 15, y); y += 15;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : CGI art. 219 (IS), CGI art. 197 (IR), Loi de finances 2026', 15, y);
    doc.save('simulation-is-vs-ir-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Paramètres
            </CardTitle>
            <CardDescription>Comparez IS et IR sur votre bénéfice</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Bénéfice imposable annuel (€)</Label>
              <Input data-testid="benefice-input" type="text" inputMode="numeric" value={inputs.benefice}
                onChange={(e) => handleInputChange('benefice', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="60000" className="mt-1" />
            </div>
            <div>
              <Label>Nombre de parts fiscales</Label>
              <Input data-testid="parts-input" type="text" inputMode="decimal" value={inputs.parts}
                onChange={(e) => handleInputChange('parts', e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="1" className="mt-1" />
            </div>
            <div>
              <Label>Dividendes prévus (si IS) (€)</Label>
              <Input data-testid="dividendes-input" type="text" inputMode="numeric" value={inputs.dividendes}
                onChange={(e) => handleInputChange('dividendes', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="20000" className="mt-1" />
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>CGI art. 219 : IS 15% jusqu'à 42 500€, 25% au-delà</li>
                    <li>CGI art. 197 : Barème IR 2026 (0/11/30/41/45%)</li>
                    <li>Flat tax 30% sur dividendes (CGI art. 200 A)</li>
                    <li>Loi de finances 2026 - Revalorisation +0.9%</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-center">Option IR</CardTitle>
            <Badge className={`mx-auto ${results.meilleur === 'IR' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {results.meilleur === 'IR' ? 'Recommandé' : 'Impôt sur le Revenu'}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-600 mb-1">Bénéfice imposable</p>
              <p className="text-xl font-bold text-blue-900">{parseInt(inputs.benefice).toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <p className="text-xs text-red-600 mb-1">Impôt sur le revenu</p>
              <p className="text-xl font-bold text-red-900">-{results.impotIR?.toLocaleString()}€</p>
              <p className="text-xs text-red-500">Taux effectif : {results.tauxIR}%</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200 text-center">
              <p className="text-xs text-green-600 mb-1">Net après impôt</p>
              <p className="text-2xl font-black text-green-900" data-testid="result-net-ir">{results.netIR?.toLocaleString()}€</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-center">Option IS</CardTitle>
            <Badge className={`mx-auto ${results.meilleur === 'IS' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {results.meilleur === 'IS' ? 'Recommandé' : 'Impôt sur les Sociétés'}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-600 mb-1">Bénéfice imposable</p>
              <p className="text-xl font-bold text-blue-900">{parseInt(inputs.benefice).toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <p className="text-xs text-red-600 mb-1">IS + Flat Tax dividendes</p>
              <p className="text-xl font-bold text-red-900">-{results.totalImpotsIS?.toLocaleString()}€</p>
              <p className="text-xs text-red-500">IS: {results.impotIS?.toLocaleString()}€ + FT: {results.flatTax?.toLocaleString()}€</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200 text-center">
              <p className="text-xs text-green-600 mb-1">Net global (dividendes + reste)</p>
              <p className="text-2xl font-black text-green-900" data-testid="result-net-is">{results.netIS?.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg text-center">
              <p className="text-xs text-purple-600 mb-1">Dont reste en société</p>
              <p className="text-lg font-bold text-purple-900">{results.resteEnSociete?.toLocaleString()}€</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Verdict */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className={`p-4 rounded-lg flex-1 text-center ${results.meilleur === 'IS' ? 'bg-green-50 border-2 border-green-300' : results.meilleur === 'IR' ? 'bg-red-50' : 'bg-gray-50'}`}>
              <p className="text-lg font-bold">{results.avantageIS > 0 ? `L'IS vous fait gagner ${results.avantageIS?.toLocaleString()}€` : results.avantageIS < 0 ? `L'IR vous fait gagner ${Math.abs(results.avantageIS)?.toLocaleString()}€` : 'Les deux options sont similaires'}</p>
            </div>
            <div className="grid grid-cols-3 gap-3 w-full md:w-auto">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center mt-4">Sources : CGI art. 219, 197, 200 A | Loi de finances 2026 | service-public.fr | impots.gouv.fr</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ISvsIRSimulator;
